/*
 * What the Farwings assistant says, and where it can take the visitor.
 * Shared by the server (api/chat.js) and the browser fallback (src/lib/bot.js).
 *
 * Rules:
 *  - Only facts that are on this website.
 *  - Never a price, estimate, discount or payment term: every pricing question
 *    goes to the team for a proper quote (see PRICE_WALL and guardPrices()).
 *  - An answer may carry `go: { to, client }`: the chat widget then takes the
 *    visitor there (route, section anchor, or a client on the clients ring).
 *
 * Intent ids match bot-model/intents.py.
 */
import { SERVICES, PROJECTS, FAQ, PROCESS, TESTIMONIALS, WHY_CHOOSE_US, STATS, STACK_GROUPS } from '../constants/index.js';
import { CLIENT_META } from '../constants/clientMeta.js';

export const CONTACT = {
  phones: ['+91 70430 31553', '+91 88668 23010'],
  whatsapp: 'https://wa.me/917043031553',
  email: 'farwingsolutions@gmail.com',
};

const svc = (id) => SERVICES.find((s) => s.id === id);
const serviceLink = (id) => ({ label: svc(id).title, to: svc(id).link });
const faq = (re) => FAQ.find((f) => re.test(f.question))?.answer || '';
const reviewFor = (projectId) => TESTIMONIALS.find((t) => t.projectId === projectId);

/* ---------------- pricing wall ---------------- */
const PRICE_WALL = {
  text:
    'I can’t share prices, estimates or discounts in this chat. Every project is quoted by the team after they understand what you need. Send your requirements and you’ll get a plan, timeline and price within 24 hours. The consultation is free.',
  links: [{ label: 'Request a quote', to: '/contact' }],
  whatsapp: true,
};

// any money figure in a reply (from the optional LLM, or a future edit) is blocked
const MONEY = /(?:₹|\$|€|£|\b(?:rs\.?|inr|usd|aed|kwd|bhd|dirhams?|rupees?|dollars?|lakhs?|lacs?|k\s?(?:inr|usd))\b)\s*\d|\d[\d,.]*\s*(?:k\b|₹|\$|rs\b|inr\b|usd\b|aed\b|kwd\b|bhd\b|dirhams?\b|rupees?\b|dollars?\b|lakhs?\b|lacs?\b|per\s+(?:hour|month|page))/i;
export function guardPrices(text) {
  return typeof text === 'string' && MONEY.test(text) ? PRICE_WALL.text : text;
}
const PRICE_WORDS = /\b(price|prices|pricing|priced|cost|costs|costing|charge|charges|charged|fee|fees|rate|rates|budget|quote|quotation|estimate|discount|cheap|cheaper|expensive|afford|affordable|payment|pay|paid|advance|retainer|kitna|kitne|paise|kharcha|daam)\b|how much(?! time)|₹|\$/i;
export const isPriceQuestion = (text) => PRICE_WORDS.test(text);

/* ---------------- where things are on the site ---------------- */
export const PLACES = {
  home: { label: 'Top of the page', to: '/#home' },
  services: { label: 'Services', to: '/#services' },
  work: { label: 'Our clients', to: '/#work' },
  process: { label: 'How we work', to: '/#process' },
  reviews: { label: 'Reviews', to: '/#testimonials' },
  stack: { label: 'Tech stack', to: '/#techstack' },
  faq: { label: 'FAQ', to: '/#faq' },
  blog: { label: 'Blog', to: '/blog' },
  contact: { label: 'Contact form', to: '/#contact' },
  servicesPage: { label: 'All services', to: '/services' },
  projectsPage: { label: 'Clients page', to: '/projects' },
  stackPage: { label: 'Tech stack page', to: '/tech-stack' },
  reviewsPage: { label: 'Reviews page', to: '/testimonials' },
  contactPage: { label: 'Contact page', to: '/contact' },
};

// visitor words -> client id (ids are the PROJECTS ids)
const CLIENT_NAMES = [
  [/\bmishkat\b|\balaqmar\b/i, 8],
  [/\bsuper\s?tech\b|supertech/i, 1],
  [/\baabex\b|\babex\b/i, 7],
  [/travel\s?mart/i, 4],
  [/\bsabta\b/i, 2],
  [/mumineen|\bdua app\b/i, 3],
];
const clientId = (text) => CLIENT_NAMES.find(([re]) => re.test(text))?.[1] ?? null;

// visitor words -> service id
const SERVICE_NAMES = [
  [/\bweb(site)?s?\b.*\b(service|page|development)\b|web development/i, 1],
  [/\bai\b|automation/i, 2],
  [/mobile|\bapps?\b/i, 3],
  [/custom software|\bsoftware\b/i, 4],
  [/\bsaas\b|\bmvp\b/i, 5],
];

/** everything about one client, and a ride to it on the clients ring */
export function clientAnswer(text) {
  const id = clientId(text);
  if (id == null) return null;
  const p = PROJECTS.find((x) => x.id === id);
  if (!p) return null;
  const m = CLIENT_META[id] || {};
  const r = reviewFor(id);
  const lines = [
    `${p.title}${m.place ? ` (${m.place})` : ''}`,
    m.problem ? `Problem: ${m.problem}` : '',
    m.built ? `What we built: ${m.built}` : '',
    p.tags?.length ? `Built with: ${p.tags.join(', ')}` : '',
    r ? `“${r.content.length > 180 ? r.content.slice(0, 177) + '…' : r.content}” — ${r.name}, ${r.role}` : '',
  ].filter(Boolean);
  const links = [{ label: `Show ${p.title}`, to: '/#work', client: id }];
  if (p.liveUrl) links.push({ label: 'Visit live site', href: p.liveUrl });
  return { text: lines.join('\n'), links, go: { to: '/#work', client: id } };
}

/* ---------------- answers per intent ---------------- */
export const ANSWERS = {
  services: () => ({
    text: 'Farwings Tech Solution builds:\n' + SERVICES.map((s) => `• ${s.title}`).join('\n') + '\nWhich one are you thinking about?',
    links: [PLACES.services, PLACES.servicesPage],
  }),
  web: () => ({ text: `${svc(1).description} Every site is built for speed and Google from day one.`, links: [serviceLink(1)] }),
  mobile: () => ({ text: `${svc(3).description} One codebase covers both Android and iOS.`, links: [serviceLink(3)] }),
  ai: () => ({ text: faq(/ai automation/i) || svc(2).description, links: [serviceLink(2)] }),
  saas: () => ({ text: svc(5).description, links: [serviceLink(5)] }),
  software: () => ({ text: svc(4).description, links: [serviceLink(4)] }),
  price: () => PRICE_WALL,
  time: () => ({ text: faq(/how long/i), links: [{ label: 'Plan my project', to: '/#contact' }] }),
  work: () => ({
    text:
      'Our clients:\n' +
      PROJECTS.map((p) => `• ${p.title}${CLIENT_META[p.id]?.place ? ` (${CLIENT_META[p.id].place})` : ''}`).join('\n') +
      '\nAsk me about any of them, or I can take you to the clients section.',
    links: [PLACES.work],
    go: { to: '/#work' },
  }),
  reviews: () => ({
    text:
      'What clients say:\n' +
      TESTIMONIALS.map((t) => `• ${t.name}, ${t.role}`).join('\n') +
      '\nTaking you to the reviews.',
    links: [PLACES.reviews],
    go: { to: '/#testimonials' },
  }),
  why: () => ({
    text:
      WHY_CHOOSE_US.map((w) => `• ${w.title}: ${w.description}`).join('\n') +
      '\n' +
      STATS.map((s) => `${s.value}${s.suffix} ${s.label.toLowerCase()}`).join(' · '),
    links: [PLACES.work, PLACES.reviews],
  }),
  blog: () => ({ text: 'Our blog has plain-language guides on websites, apps, AI automation and getting found on Google.', links: [PLACES.blog], go: { to: '/blog' } }),
  faq: () => ({ text: FAQ.map((f) => `• ${f.question}`).join('\n') + '\nAsk me any of these, or open the FAQ.', links: [PLACES.faq] }),
  contact: () => ({
    text: `You can reach the team directly:\nPhone: ${CONTACT.phones.join(' or ')}\nEmail: ${CONTACT.email}\nWe reply within 24 hours on business days, usually much sooner on WhatsApp.`,
    links: [PLACES.contact],
    whatsapp: true,
  }),
  location: () => ({ text: 'We are based in Gujarat, India, and work with clients in India, the UAE, Kuwait, Bahrain and beyond.' }),
  seo: () => ({ text: faq(/seo/i), links: [serviceLink(1)] }),
  tech: () => ({
    text: STACK_GROUPS.map((g) => `• ${g.name}: ${g.items.join(', ')}`).join('\n'),
    links: [PLACES.stack],
  }),
  process: () => ({
    text: 'It works in four steps:\n' + PROCESS.map((p) => `• ${p.title}: ${p.description}`).join('\n'),
    links: [PLACES.process],
  }),
  support: () => ({
    text: `Yes. We stay on after launch. ${PROCESS[PROCESS.length - 1].description}`,
    links: [{ label: 'Talk to us', to: '/#contact' }],
  }),
  hello: () => ({ text: 'Hi! I am the Farwings assistant. Ask me about our services, clients, reviews or how we work, or tell me where on the site to take you.' }),
  thanks: () => ({ text: 'Happy to help. If you want to go ahead, the quickest way is a WhatsApp message to the team.', whatsapp: true }),
  who: () => ({
    text: 'I am the Farwings assistant, a small AI model trained on this website. I can answer questions and take you anywhere on the site. For anything else, a real person is one message away.',
    whatsapp: true,
  }),
  OFF: () => ({
    text: 'I can only help with Farwings: our services, clients, reviews, process and how to reach the team. What are you looking to build?',
    links: [PLACES.services],
  }),
  fallback: () => ({
    text: 'I am not sure I understood. I can tell you about our services, clients, reviews, process and tech, or take you to any part of the site.',
    links: [PLACES.work, PLACES.contact],
    whatsapp: true,
  }),
};

/* ---------------- navigation requests ---------------- */
const TARGETS = [
  [/\b(contact|reach|enquiry|inquiry|form|quote form)\b/i, 'contact'],
  [/\b(clients?|projects?|work|portfolio|case stud(y|ies))\b/i, 'work'],
  [/\b(reviews?|testimonials?|feedback)\b/i, 'reviews'],
  [/\b(process|how you work|steps)\b/i, 'process'],
  [/\b(tech(nology)? ?stack|technolog(y|ies)|stack)\b/i, 'stack'],
  [/\b(faq|questions)\b/i, 'faq'],
  [/\b(blog|articles?|posts?)\b/i, 'blog'],
  [/\b(services?|offerings?)\b/i, 'services'],
  [/\b(home ?page|home|main page|top|start)\b/i, 'home'],
];
const GO = /\b(take me|go to|goto|open|show( me)?|navigate|bring me|jump to|scroll to|visit|where is|where are|link to|lead me|move to|le chalo|dikhao|kholo)\b/i;

/** "take me to reviews" / "show me sabta" -> answer that moves the visitor, or null */
export function navAnswer(text) {
  if (!GO.test(text)) return null;
  if (isPriceQuestion(text)) return null; // "show me your prices" goes to the pricing wall
  const c = clientAnswer(text);
  if (c) return c;
  for (const [re, id] of SERVICE_NAMES) {
    if (re.test(text) && /\b(service|page|development)\b/i.test(text)) {
      const s = svc(id);
      return { text: `Opening ${s.title}.`, links: [serviceLink(id)], go: { to: s.link } };
    }
  }
  const hit = TARGETS.find(([re]) => re.test(text));
  if (!hit) return null;
  const place = PLACES[hit[1]];
  return { text: `Sure, taking you to ${place.label.toLowerCase()}.`, links: [place], go: { to: place.to } };
}

/** answers that do not need the model: navigation, a named client, or a price question */
export function directAnswer(text) {
  if (isPriceQuestion(text) && !/\b(review|testimonial)s?\b/i.test(text)) return { ...PRICE_WALL, intent: 'price' };
  const nav = navAnswer(text);
  if (nav) return { ...nav, intent: 'nav' };
  const c = clientAnswer(text);
  if (c) return { ...c, intent: 'client' };
  return null;
}

export function answerFor(intent) {
  return (ANSWERS[intent] || ANSWERS.fallback)();
}
